import type { SafeHtml } from '@angular/platform-browser';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import type { Image } from '@core-mkt/interfaces/image';
import { IconService } from '@core-mkt/services/iconService/icon.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { Infographics } from './infographics';
import type { InfographicImageUrls } from './infographics';

const SIZE_ORDER: ReadonlyArray<keyof InfographicImageUrls> = ['xs', 'sm', 'md', 'lg', 'xl'];

function getSuffixFromUrl(url: string): keyof InfographicImageUrls | null {
  const filename = url.split('/').at(-1) ?? '';
  const nameWithoutExt = filename.includes('.') ? filename.substring(0, filename.lastIndexOf('.')) : filename;
  const match = /-(xs|sm|md|lg|xl)$/.exec(nameWithoutExt);
  return match ? (match[1] as keyof InfographicImageUrls) : null;
}

function buildUploadedMap(images: Image[]): Map<keyof InfographicImageUrls, string> {
  const uploaded = new Map<keyof InfographicImageUrls, string>();
  for (const img of images) {
    const suffix = getSuffixFromUrl(img.url);
    if (suffix) {
      uploaded.set(suffix, img.url);
    }
  }
  return uploaded;
}

function fillForward(uploaded: Map<keyof InfographicImageUrls, string>): InfographicImageUrls {
  const result = {} as InfographicImageUrls;
  let lastUrl = uploaded.get(SIZE_ORDER.find((s) => uploaded.has(s)) ?? 'xs') ?? '';

  for (const size of SIZE_ORDER) {
    lastUrl = uploaded.get(size) ?? lastUrl;
    result[size] = lastUrl;
  }
  return result;
}

/**
 * Maps uploaded images to breakpoints using filename suffixes (-xs, -sm, -md, -lg, -xl).
 * Missing sizes fill from the nearest uploaded neighbour (forward and backward),
 * so uploading only -lg shows that image at all breakpoints.
 * Falls back to using the first image for all sizes when no suffixes are found.
 * Returns null when no images are uploaded.
 */
function calculateInfographicImages(images: Image[]): InfographicImageUrls | null {
  if (!images?.length) {
    return null;
  }

  const uploaded = buildUploadedMap(images);

  if (uploaded.size === 0) {
    const fallback = images[0].url;
    return { xs: fallback, sm: fallback, md: fallback, lg: fallback, xl: fallback };
  }

  return fillForward(uploaded);
}

@Component({
  selector: 'infographics',
  templateUrl: './infographics.component.html',
  styleUrls: ['./infographics.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfographicsComponent implements OnInit {
  @Input() data!: Infographics;

  isOpen = false;
  openIcon: SafeHtml | null = null;
  closeIcon: SafeHtml | null = null;

  constructor(
    private readonly redactor: WysiwygRedactorService,
    private readonly iconService: IconService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  get infographicImages(): InfographicImageUrls | null {
    return calculateInfographicImages(this.data?.infographicImage ?? []);
  }

  get isResponsiveMode(): boolean {
    return this.infographicImages !== null;
  }

  get responsiveAltText(): string {
    const img: Image | undefined = this.data?.infographicImage?.[0];
    return img?.altText || img?.title || 'Infographic';
  }

  get hasDescription(): boolean {
    return !!this.data?.longDescription;
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    if (this.data?.longDescription) {
      this.data.longDescription = this.redactor.bypassSanitizer(this.data.longDescription);
    }
    this.openIcon = this.redactor.bypassSanitizer(this.iconService.getBrandIcon('accordion-open')?.icon);
    this.closeIcon = this.redactor.bypassSanitizer(this.iconService.getBrandIcon('accordion-close')?.icon);
  }
}
